import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ExerciseScoreService } from '../../services/ExerciseScoreService';
import { ChunkyButton, GameProgressBar, Confetti, StarRow, ShakeWidget, FloatingXpBadge } from '../../components/GameUI';

// Flip-card vocabulary memory game — port of the mobile app's
// MemoryCardsScreen (lib/games/memory_cards/memory_cards_screen.dart).
// Tap two cards to reveal a word and its matching emoji; a match locks the
// pair face-up in green, a mismatch shakes both cards then flips them back.
// Uses the same 3-D flip technique as PracticePage's Flashcard component.

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildCards(pairs) {
  const cards = [];
  for (const pair of pairs) {
    cards.push({ pairId: pair.id, isWord: true, display: pair.word });
    cards.push({ pairId: pair.id, isWord: false, display: pair.emoji });
  }
  return shuffle(cards);
}

function formatElapsed(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function FlipCard({ card, faceUp, matched, wrong, dir, onClick }) {
  const isEmoji = !card.isWord;
  return (
    <ShakeWidget trigger={wrong}>
      <div
        onClick={onClick}
        className="cursor-pointer select-none"
        style={{ perspective: '1000px', aspectRatio: '0.8' }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            transformStyle: 'preserve-3d',
            transition: 'transform 350ms ease',
            transform: faceUp ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          {/* face-down */}
          <div
            style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
            className="absolute inset-0 flex items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-2xl text-white shadow-md"
          >
            ❔
          </div>
          {/* revealed */}
          <div
            dir={card.isWord ? dir : 'ltr'}
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              background: matched ? '#D7FFB8' : '#FFFFFF',
              borderColor: matched ? '#58CC02' : '#E2E8F0',
              color: matched ? '#2B6B00' : '#0F172A',
            }}
            className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-2xl border-[2.5px] p-1.5 text-center"
          >
            <span className={isEmoji ? 'text-3xl' : 'line-clamp-2 text-xs font-extrabold leading-tight'}>
              {card.display}
            </span>
          </div>
        </div>
      </div>
    </ShakeWidget>
  );
}

export default function MemoryCardsGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { pairs = [], targetLang = { dir: 'ltr' }, langPair = 'guest' } = state || {};

  const [cards, setCards] = useState(() => buildCards(pairs));
  const [faceUpIndices, setFaceUpIndices] = useState([]);
  const [matchedPairIds, setMatchedPairIds] = useState(new Set());
  const [wrongIndices, setWrongIndices] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [done, setDone] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [columns, setColumns] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth < 380 ? 3 : 4
  ));

  const rewardedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!pairs.length) navigate('/games', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function onResize() {
      setColumns(window.innerWidth < 380 ? 3 : 4);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  function startTimerIfNeeded() {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
  }

  function stopTimer() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  const percent = attempts === 0
    ? 100
    : Math.round(Math.max(0, Math.min(100, (pairs.length / attempts) * 100)));
  const stars = ExerciseScoreService.starsForPercent(percent);
  const xpEarned = pairs.length * 4 + (stars === 3 ? 10 : 0);

  function awardRewards() {
    if (rewardedRef.current) return;
    rewardedRef.current = true;
    StreakService.recordActivity();
    XpService.addXp(xpEarned);
    if (stars === 3) SoundEffectService.playCelebration();
  }

  // Flip everything face-down and reset counters for a fresh attempt at the
  // same set of pairs (mirrors the Dart _buildCards() reset used by "Try Again").
  function retry() {
    stopTimer();
    rewardedRef.current = false;
    setCards(buildCards(pairs));
    setFaceUpIndices([]);
    setMatchedPairIds(new Set());
    setWrongIndices(new Set());
    setBusy(false);
    setAttempts(0);
    setElapsedSeconds(0);
    setDone(false);
    setHasInteracted(false);
  }

  function tapCard(index) {
    if (busy || done) return;
    const card = cards[index];
    if (matchedPairIds.has(card.pairId)) return;
    if (faceUpIndices.includes(index)) return;

    if (!hasInteracted) setHasInteracted(true);
    startTimerIfNeeded();

    const nextFaceUp = [...faceUpIndices, index];
    setFaceUpIndices(nextFaceUp);
    if (nextFaceUp.length < 2) return;

    setBusy(true);
    setAttempts((a) => a + 1);
    const [ia, ib] = nextFaceUp;
    const a = cards[ia];
    const b = cards[ib];

    if (a.pairId === b.pairId) {
      SoundEffectService.playCorrect();
      if (navigator.vibrate) navigator.vibrate(15);
      LearningProgressService.record(langPair, 'vocabulary', 1, { activityId: `memorycards:${a.pairId}` });
      setTimeout(() => {
        setMatchedPairIds((prev) => {
          const next = new Set(prev);
          next.add(a.pairId);
          return next;
        });
        setFaceUpIndices([]);
        setBusy(false);
      }, 220);
    } else {
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
      setWrongIndices(new Set(nextFaceUp));
      setTimeout(() => {
        setFaceUpIndices([]);
        setWrongIndices(new Set());
        setBusy(false);
      }, 650);
    }
  }

  // Win condition: every pair matched. Kept as its own effect (rather than
  // inline in the setTimeout above) so it reacts to the committed state,
  // matching the Dart version's check inside its setState callback.
  useEffect(() => {
    if (!done && pairs.length && matchedPairIds.size === pairs.length) {
      setDone(true);
      stopTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedPairIds]);

  useEffect(() => {
    if (done) awardRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  if (!pairs.length) return null;

  const elapsedLabel = formatElapsed(elapsedSeconds);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-4 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50">
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('games.memoryCards')}</h1>
          <span className="w-16" />
        </div>

        {done ? (
          <div className="relative flex flex-col items-center py-10 text-center">
            {stars === 3 && <Confetti />}
            <span className="text-6xl">{stars === 3 ? '🏆' : '🎉'}</span>
            <h2 className="mt-3 text-2xl font-black text-slate-900">
              {t('games.memoryCardsComplete', 'Memory Match complete!')}
            </h2>
            <div className="mt-2">
              <StarRow count={stars} />
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {t('games.memoryCardsElapsed', 'Time')}: {elapsedLabel}
            </p>
            <div className="mt-3">
              <FloatingXpBadge xp={xpEarned} />
            </div>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton color="#4F46E5" onClick={retry} className="w-full py-4 text-center text-white">
                {t('games.tryAgain')}
              </ChunkyButton>
              <button onClick={() => navigate('/games')} className="mt-3 w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">
                {t('games.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {!hasInteracted && (
              <div className="mb-3 flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-3.5 py-2.5">
                <span className="text-lg">🃏</span>
                <p className="text-xs font-bold text-indigo-700">
                  {t('games.memoryCardsHint', 'Tap two cards to find a matching pair!')}
                </p>
              </div>
            )}

            <div className="mb-2 flex items-center justify-between">
              <span className="text-base font-black text-slate-900">{matchedPairIds.size}/{pairs.length}</span>
              <span className="flex items-center gap-1 text-sm font-extrabold text-slate-500">
                <span aria-hidden="true">⏱</span> {elapsedLabel}
              </span>
            </div>
            <GameProgressBar value={pairs.length ? matchedPairIds.size / pairs.length : 0} color="#4F46E5" />

            <div
              className="mt-5 grid gap-2.5"
              style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
            >
              {cards.map((card, i) => {
                const matched = matchedPairIds.has(card.pairId);
                const faceUp = matched || faceUpIndices.includes(i);
                const wrong = wrongIndices.has(i);
                return (
                  <FlipCard
                    key={i}
                    card={card}
                    faceUp={faceUp}
                    matched={matched}
                    wrong={wrong}
                    dir={targetLang.dir}
                    onClick={() => tapCard(i)}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
